import React, { useState, useEffect } from 'react';
import '../styles/ProofHeader.css';

interface ProofHeaderProps {
  title?: string;
  session?: Session | null;
  isUnlocked?: boolean;
  onEndAccess?: () => void;
}

interface Session {
  expirationDate?: string;
  evidenceType?: string;
}

const ProofHeader: React.FC<ProofHeaderProps> = ({ 
  title = "Proof of Consent",
  session,
  isUnlocked = false,
  onEndAccess
}) => {
  
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  
  // Calculate and format time remaining until localStorage unlock expires
  const calculateTimeRemaining = () => {
    if (session?.evidenceType === 'native_submission') return 'Access ends when you close or reload this page';
    const unlockTimestampKey = `verfi_unlock_timestamp_${window.location.pathname.split('/').pop()}`;
    const unlockTimestamp = localStorage.getItem(unlockTimestampKey);
    
    if (!unlockTimestamp) return 'Unknown';
    
    const unlockTime = new Date(parseInt(unlockTimestamp));
    const expirationTime = new Date(unlockTime.getTime() + 24 * 60 * 60 * 1000); // 24 hours later
    const now = new Date();
    const timeDiff = expirationTime.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      return 'Access expired';
    }
    
    // Calculate hours, minutes, seconds
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
    
    // Format as HH:MM:SS
    const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    
    return `Your restricted access expires in ${formattedTime}`;
  };
  
  // Update timer every second
  useEffect(() => {
    if (!isUnlocked || !session) return;
    
    // Initial calculation
    setTimeRemaining(calculateTimeRemaining());
    
    // Set up interval to update every second
    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 1000);
    
    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [isUnlocked, session]);
  
  return (
    <header className="proof-header">
      <div className="proof-header-content">
        <div className="proof-header-brand">
          <div className="proof-header-logo">
            <img src="/logo.svg" alt="Verfi" className="logo-image" />
          </div>
          <div className="proof-header-service">
            <span className="service-text">Proof</span>
          </div>
        </div>
        <div className="proof-header-title">
          <h1>{title}</h1>
          {isUnlocked && session && (
            <div className="proof-header-expiration">
              <span className="expiration-text">{timeRemaining}</span>
              {onEndAccess && (
                <button 
                  className="end-access-button" 
                  onClick={onEndAccess}
                  title="End your access to this session"
                >
                  End Access
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default ProofHeader;
